import React from 'react';
import { Icon } from './Icon.jsx';

/**
 * LangToggle — shared segmented EN | عربي language switch.
 *
 * Replaces the old ambiguous single-label pill (which only ever showed the
 * *other* language, so users couldn't tell current vs. target) with a
 * two-segment control that always shows both options, active one
 * highlighted. Used on light surfaces (storefront header, admin content,
 * Settings, AdminLogin card) and dark surfaces (AdminSidebar, vendor/
 * retailer public pages).
 *
 * `setLang` is called with the concrete new value ('en' | 'ar'), which is
 * compatible both with a plain value-setter (`setLang('ar')`) and a React
 * state updater form used elsewhere in the app (`setLang(l => …)` still
 * works fine since we always pass the literal string, not a function).
 */
const SIZES = {
  sm: { padding: '4px 10px', fontSize: 11, icon: 12 },
  md: { padding: '5px 12px', fontSize: 12.5, icon: 14 },
};

export function LangToggle({ lang, setLang, variant = 'light', size = 'md' }) {
  const dark = variant === 'dark';
  const seg = SIZES[size] || SIZES.md;

  const pick = (l) => {
    setLang(l);
    try { localStorage.setItem('sedra_lang', l); } catch {}
  };

  const segStyle = (active) => ({
    border: 'none',
    borderRadius: 'var(--radius-pill)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: seg.padding,
    fontSize: seg.fontSize,
    fontWeight: active ? 700 : 500,
    // Deep pistachio fill: reads as "selected" even on the pale --sedra-nav
    // surfaces (raw --sedra-pistachio was only ~1.65:1 against them).
    background: active ? 'var(--sedra-pistachio-deep)' : 'transparent',
    color: active ? 'var(--sedra-cream)' : (dark ? 'rgba(255,255,255,0.75)' : 'var(--sedra-ink-muted)'),
    lineHeight: 1,
    whiteSpace: 'nowrap',
  });

  return (
    <div
      role="group"
      aria-label="Language / اللغة"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: 3,
        minHeight: 34,
        boxSizing: 'border-box',
        borderRadius: 'var(--radius-pill)',
        border: `1px solid ${dark ? 'rgba(255,255,255,0.18)' : 'var(--sedra-line)'}`,
        background: dark ? 'rgba(255,255,255,0.05)' : 'transparent',
      }}
    >
      <Icon
        name="Globe"
        size={seg.icon}
        color={dark ? 'rgba(255,255,255,0.6)' : 'var(--sedra-ink-muted)'}
        style={{ marginInlineStart: 6, marginInlineEnd: 2, flexShrink: 0 }}
      />
      <button type="button" onClick={() => pick('en')} aria-pressed={lang === 'en'} style={segStyle(lang === 'en')}>EN</button>
      <button type="button" onClick={() => pick('ar')} aria-pressed={lang === 'ar'} style={segStyle(lang === 'ar')}>عربي</button>
    </div>
  );
}
